import { useCallback, useEffect, useRef } from "react";

/**
 * Column-resize drag, with listeners that survive nothing.
 *
 * ITS OWN MODULE because the correctness argument below is the file's whole
 * content and has nothing to do with rendering a header cell — and because
 * lifting it out is what keeps `TableHeaderColumns.tsx` under the 300-line cap.
 *
 * THE BUG IT FIXES. A resize attaches `mousemove` + `mouseup` to `document`,
 * and the original code detached them ONLY from inside the `mouseup` handler.
 * If the header unmounted mid-drag — a route change, a virtualized re-render —
 * `mouseup` never reached it and both listeners stayed on `document` for the
 * life of the tab, holding the callbacks and the column state with them.
 *
 * Two rules make it correct, and both are easy to write wrong:
 *   • The exact function references that were added are remembered and reused.
 *     `removeEventListener` matches on IDENTITY, so passing a fresh arrow
 *     detaches nothing while reading like a correct cleanup.
 *   • Detach runs from an unmount effect as well as from `mouseup`, and
 *     defensively before each new drag, so two drags can never stack.
 */
export function useColumnResizeDrag(options: {
  onResize: (deltaX: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}): (event: { preventDefault: () => void; clientX: number }) => void {
  const { onResize, onDragStart, onDragEnd } = options;

  const attachedRef = useRef<{ move: (e: MouseEvent) => void; up: () => void } | null>(null);

  const detach = useCallback(() => {
    const attached = attachedRef.current;
    if (!attached) return;
    document.removeEventListener("mousemove", attached.move);
    document.removeEventListener("mouseup", attached.up);
    attachedRef.current = null;
  }, []);

  useEffect(() => detach, [detach]);

  return useCallback(
    (event: { preventDefault: () => void; clientX: number }) => {
      event.preventDefault();
      onDragStart();
      const startX = event.clientX;

      const handleMouseMove = (e: MouseEvent): void => {
        onResize(e.clientX - startX);
      };
      const handleMouseUp = (): void => {
        onDragEnd();
        detach();
      };

      // Never stack two drags' listeners if a previous one somehow never ended.
      detach();
      attachedRef.current = { move: handleMouseMove, up: handleMouseUp };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [detach, onDragEnd, onDragStart, onResize],
  );
}
